/**
 * QUIPWA — notes ⇄ Drive
 *
 * Drive layout (unchanged, readable by Obsidian or any editor):
 *
 *   QUIPWA/Quipwa_Notes/
 *     algebra_basica.md            ← standalone note
 *     Desarrollo web/              ← a Long Course
 *       _index.md                  ← the course note
 *       modulo_1.md, html.md…      ← its modules
 *
 * pull()  — the SyncEngine's notes phase. Lists the notes folder and the
 *           course folders (2 requests + 1 per 40 courses), compares each file's
 *           `version` with the one this device last saw, and downloads only
 *           what changed. Replaces `notes_index.json`, which had to be
 *           downloaded on every search and rebuilt by downloading every note.
 *
 * push(id) — the `note.push` outbox handler. Creates, updates, moves or
 *           trashes one note. If Drive changed since this device's base, the
 *           two versions are merged (text-merge.ts); if they collide, Drive's
 *           text stays in the note and this device's becomes a conflict copy.
 *
 * Every write back to the table goes through `updateIf(id, rev, …)`, so an
 * upload never overwrites what the user typed while it was in flight.
 */
import type { DriveFile, DriveService } from '../drive/drive-service'
import { FOLDER_MIME } from '../drive/drive-service'
import { isDriveError } from '../drive/drive-errors'
import { q } from '../drive/drive-query'
import type { Outbox } from '../local/outbox'
import { courseFolderName, withFrontmatterTitle } from './course-promotion'
import type { DriveIdPool } from './drive-id-pool'
import { NOTE_PUSH } from './local-notes-repository'
import { COURSE_INDEX_FILENAME, isDriveId, newLocalNoteId, noteFilename } from './note-model'
import { fieldsPatch, type NoteRow, type NotesTable } from './notes-table'
import { mergeText } from './text-merge'

export type NotesDrive = Pick<
  DriveService,
  | 'ensureNotesFolder' | 'listAll' | 'downloadText' | 'createFile' | 'updateFileContent' | 'updateMetadata'
  | 'getFileMeta' | 'findById' | 'moveFile' | 'trashFile' | 'createFolder' | 'generateIds'
>

export interface NoteConflictNotice {
  id: string
  noteId: string
  title: string
  copyNoteId: string
  detectedAt: number
}

export interface NotesSyncReport {
  /** Notes added, updated or removed locally by this pull. */
  changed: number
  downloaded: number
}

export interface NotesDriveSyncDeps {
  drive: NotesDrive
  table: NotesTable
  outbox: Pick<Outbox, 'enqueue'>
  idPool: DriveIdPool
  /** The table changed (refresh the UI). */
  onChanged: () => void
  onConflict: (notice: NoteConflictNotice) => void
  now?: () => number
  /** Parallel downloads during a pull. */
  concurrency?: number
}

const MARKDOWN_MIME = 'text/markdown'
const PARENTS_PER_QUERY = 40
const MAX_PUSH_DEPTH = 4

interface Location {
  parent_id: string | null
  folder_id: string | null
}

/** Stable note id of a Drive file (see note-model.ts). */
export function noteIdOf(file: Pick<DriveFile, 'id' | 'appProperties'>): string {
  return file.appProperties?.['quipwaId'] ?? file.id
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

async function mapLimit<T>(items: readonly T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let next = 0
  const worker = async () => {
    while (next < items.length) await fn(items[next++]!)
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
}

export class NotesDriveSync {
  private readonly deps: NotesDriveSyncDeps
  private readonly now: () => number

  constructor(deps: NotesDriveSyncDeps) {
    this.deps = deps
    this.now = deps.now ?? Date.now
  }

  // ── pull ──────────────────────────────────────────────────────────────────

  async pull(): Promise<NotesSyncReport> {
    const { drive, table } = this.deps
    const notesFolder = await drive.ensureNotesFolder()

    const folders = await drive.listAll(q.and(q.inParent(notesFolder), q.mimeTypeIs(FOLDER_MIME), q.notTrashed()))
    const folderIds = new Set(folders.map((f) => f.id))
    const files: DriveFile[] = []
    for (const group of chunk([notesFolder, ...folderIds], PARENTS_PER_QUERY)) {
      files.push(...(await drive.listAll(q.and(q.anyParent(group), q.mimeTypeIsNot(FOLDER_MIME), q.notTrashed()))))
    }
    const markdown = files.filter((f) => f.name.toLowerCase().endsWith('.md'))

    // A course folder is one holding an `_index.md`; others are ignored, as before.
    const courseIndexByFolder = new Map<string, DriveFile>()
    for (const f of markdown) {
      const parent = f.parents?.[0]
      if (f.name !== COURSE_INDEX_FILENAME || !parent || !folderIds.has(parent)) continue
      const current = courseIndexByFolder.get(parent)
      if (!current || f.modifiedTime > current.modifiedTime) courseIndexByFolder.set(parent, f)
    }

    const locate = (f: DriveFile): Location | null => {
      const parent = f.parents?.[0]
      if (parent === notesFolder) return { parent_id: null, folder_id: null }
      const index = parent ? courseIndexByFolder.get(parent) : undefined
      if (!index) return null
      return index.id === f.id ? { parent_id: null, folder_id: parent! } : { parent_id: noteIdOf(index), folder_id: null }
    }

    const rows = await table.listAll()
    const byDrive = new Map(rows.filter((r) => r.drive_file_id).map((r) => [r.drive_file_id!, r]))
    const byId = new Map(rows.map((r) => [r.id, r]))
    const seen = new Set<string>()
    /** Rows matched to a Drive file in this pass (possibly under a new file id). */
    const matched = new Set<string>()
    const downloads: Array<{ file: DriveFile; row: NoteRow | null; location: Location }> = []
    let changed = 0

    for (const file of markdown) {
      const location = locate(file)
      if (!location) continue
      seen.add(file.id)
      const row = byDrive.get(file.id) ?? byId.get(noteIdOf(file)) ?? null
      if (row) matched.add(row.id)
      // Local changes not uploaded yet: the push merges them with this version.
      if (row?.dirty) continue
      const sameContent = row !== null && row.base_version === file.version && row.drive_file_id === file.id
      if (sameContent) {
        if (row.parent_id !== location.parent_id || row.folder_id !== location.folder_id || row.filename !== file.name) {
          await table.updateIf(row.id, row.local_rev, { ...location, filename: file.name })
          changed++
        }
        continue
      }
      downloads.push({ file, row, location })
    }

    await mapLimit(downloads, this.deps.concurrency ?? 4, async ({ file, row, location }) => {
      const body = await drive.downloadText(file.id)
      const patch = {
        drive_file_id: file.id,
        ...location,
        filename: file.name,
        body,
        ...fieldsPatch(body, file.name),
        base: body,
        base_version: file.version ?? null,
        modified_at: Date.parse(file.modifiedTime) || this.now(),
      }
      if (row) {
        if (await table.updateIf(row.id, row.local_rev, patch)) changed++
      } else {
        await table.insert({ id: noteIdOf(file), ...patch, local_rev: 0, dirty: 0, deleted: 0 })
        changed++
      }
    })

    // Notes whose file is gone from Drive (deleted or trashed elsewhere).
    for (const row of rows) {
      if (!row.drive_file_id || seen.has(row.drive_file_id) || matched.has(row.id) || row.deleted) continue
      if (row.dirty) {
        // Edited here, deleted there: the edit wins and the push re-creates it.
        await table.updateIf(row.id, row.local_rev, { drive_file_id: null, base: null, base_version: null })
        continue
      }
      await table.remove(row.id)
      changed++
    }

    await this.deps.idPool.refill(drive).catch((err: unknown) => console.warn('[Notes] Could not refill the id pool', err))
    if (changed > 0) this.deps.onChanged()
    return { changed, downloaded: downloads.length }
  }

  // ── push ──────────────────────────────────────────────────────────────────

  /** Upload one note's pending change. Throws to let the outbox retry later. */
  async push(id: string, depth = 0): Promise<void> {
    // Every recursive step below changes the row; a bounded depth turns any
    // unforeseen Drive answer into a retryable error instead of a hot loop.
    if (depth > MAX_PUSH_DEPTH) throw new Error(`No se pudo subir la nota ${id}`)
    const { table } = this.deps
    const row = await table.get(id)
    if (!row) return // purged meanwhile
    if (row.deleted) return this.pushDelete(row)
    if (!row.dirty) return // already pushed (e.g. as a module's parent)

    const parentFolder = await this.folderFor(row, depth)
    const rev = row.local_rev
    const content = row.body

    if (!row.drive_file_id) {
      const file = await this.createFile(row, parentFolder)
      if (!file) return this.push(id, depth + 1) // a lost earlier create already made it
      await this.settle(row.id, rev, content, file, { drive_file_id: file.id })
      return
    }

    let meta = await this.deps.drive.findById(row.drive_file_id)
    if (!meta || meta.trashed) {
      // Gone on Drive while edited here: the edit wins, upload it again.
      await table.update(row.id, { drive_file_id: null, base: null, base_version: null })
      return this.push(id, depth + 1)
    }

    const moved = !(meta.parents ?? []).includes(parentFolder)
    const renamed = meta.name !== row.filename

    if (meta.version === row.base_version) {
      // Drive has not changed since our base: plain upload.
      if (content !== row.base) {
        meta = await this.deps.drive.updateFileContent(meta.id, content, MARKDOWN_MIME, renamed && !moved ? { name: row.filename } : {})
      } else if (renamed && !moved) {
        meta = await this.deps.drive.updateMetadata(meta.id, { name: row.filename })
      }
      if (moved) meta = await this.deps.drive.moveFile(meta.id, parentFolder, row.filename)
      await this.settle(row.id, rev, content, meta)
      return
    }

    // Both sides changed: three-way merge against the last common version.
    const remote = await this.deps.drive.downloadText(meta.id)
    const merged = mergeText(row.base ?? '', content, remote)

    if (!merged.conflict) {
      if (merged.text !== remote || (renamed && !moved)) {
        meta = await this.deps.drive.updateFileContent(meta.id, merged.text, MARKDOWN_MIME, renamed && !moved ? { name: row.filename } : {})
      }
      if (moved) meta = await this.deps.drive.moveFile(meta.id, parentFolder, row.filename)
      // If the user typed meanwhile, leave the base alone: the next push merges
      // their newer text with what is now on Drive.
      await table.updateIf(row.id, rev, {
        body: merged.text, ...fieldsPatch(merged.text, row.filename),
        base: merged.text, base_version: meta.version ?? null, dirty: 0,
      })
      this.deps.onChanged()
      return
    }

    // Conflict: Drive's text stays in the note; this device's becomes a copy.
    const applied = await table.updateIf(row.id, rev, {
      body: remote, ...fieldsPatch(remote, meta.name),
      base: remote, base_version: meta.version ?? null, dirty: 0,
    })
    if (!applied) throw new Error('La nota cambió durante la sincronización; se reintentará')
    const copyId = await this.createConflictCopy(row, content)
    this.deps.onConflict({ id: crypto.randomUUID(), noteId: row.id, title: row.title, copyNoteId: copyId, detectedAt: this.now() })
    this.deps.onChanged()
  }

  /** Where a note's file belongs on Drive; creates the course folder (and course) when needed. */
  private async folderFor(row: NoteRow, depth: number): Promise<string> {
    const { drive, table } = this.deps
    if (row.parent_id) {
      let parent = await table.get(row.parent_id)
      if (!parent || parent.deleted) {
        // The course is gone: keep the module as a standalone note.
        await table.update(row.id, { parent_id: null })
        return drive.ensureNotesFolder()
      }
      if (!parent.folder_id) {
        await this.push(parent.id, depth + 1)
        parent = await table.get(parent.id)
        if (!parent?.folder_id) throw new Error('El curso todavía no tiene carpeta en Drive')
      }
      return parent.folder_id
    }
    if (row.type === 'Long Course') {
      if (row.folder_id) return row.folder_id
      const reserved = await this.deps.idPool.take()
      let folderId: string
      try {
        folderId = (await drive.createFolder(courseFolderName(row.title), await drive.ensureNotesFolder(), reserved ?? undefined)).id
      } catch (err) {
        if (!(reserved && isDriveError(err, 'conflict'))) throw err
        folderId = reserved // a lost earlier response already created it
      }
      await table.update(row.id, { folder_id: folderId })
      return folderId
    }
    return drive.ensureNotesFolder()
  }

  /** Create the note's file. Null when an earlier, lost create already made it. */
  private async createFile(row: NoteRow, parentId: string): Promise<DriveFile | null> {
    const input = {
      name: row.filename,
      parentId,
      mimeType: MARKDOWN_MIME,
      content: row.body,
      // How every device maps the file back to this note, whatever its file id.
      appProperties: { quipwaId: row.id },
    }
    if (!isDriveId(row.id)) return this.deps.drive.createFile(input)
    try {
      return await this.deps.drive.createFile({ ...input, id: row.id })
    } catch (err) {
      if (!isDriveError(err, 'conflict')) throw err
      const existing = await this.deps.drive.findById(row.id)
      if (existing && !existing.trashed) {
        // An earlier attempt created it but its response was lost. Treat it
        // as the remote version and let the merge path reconcile.
        await this.deps.table.update(row.id, { drive_file_id: row.id, base: null, base_version: null })
        return null
      }
      // The id belongs to a file deleted on another device (it sits in the
      // trash, so Drive will not reuse the id): upload a fresh file. The note
      // keeps its id through appProperties.quipwaId.
      return this.deps.drive.createFile(input)
    }
  }

  /** Record an upload. If the user edited meanwhile, the note stays dirty for the next push. */
  private async settle(id: string, rev: number, content: string, file: DriveFile, extra: Partial<NoteRow> = {}): Promise<void> {
    const patch = { ...extra, base: content, base_version: file.version ?? null }
    const clean = await this.deps.table.updateIf(id, rev, { ...patch, dirty: 0 })
    if (!clean) await this.deps.table.update(id, patch)
  }

  private async pushDelete(row: NoteRow): Promise<void> {
    if (row.type === 'Long Course' && row.folder_id) await this.deps.drive.trashFile(row.folder_id)
    else if (row.drive_file_id) await this.deps.drive.trashFile(row.drive_file_id)
    await this.deps.table.remove(row.id)
  }

  private async createConflictCopy(row: NoteRow, localBody: string): Promise<string> {
    const stamp = new Date(this.now()).toISOString().slice(0, 16).replace('T', ' ')
    const title = `${row.title} (conflicto ${stamp})`
    const body = withFrontmatterTitle(localBody, title)
    const id = (await this.deps.idPool.take()) ?? newLocalNoteId()
    await this.deps.table.insert({
      id,
      drive_file_id: null,
      parent_id: row.parent_id,
      folder_id: null,
      filename: noteFilename(title),
      ...fieldsPatch(body, noteFilename(title)),
      body,
      base: null,
      base_version: null,
      modified_at: this.now(),
      local_rev: 1,
      dirty: 1,
      deleted: 0,
    })
    await this.deps.outbox.enqueue(NOTE_PUSH, id, { id })
    return id
  }
}
